import { IEquipment } from '@/pages/Equipment/equipment.types.ts'
import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import useForceRerender from '@/shared/hooks/useForceRerender.ts'
import { ILegendPlugin, ITSDBDataRendererPlugin } from '@/shared/Charting/LineChart/lineChart.types.ts'
import { IParameter } from '@/pages/Parameters/parameters.types.ts'
import useDataRefConfigIntoRendererPluginParams from '@/shared/Charting/LineChart/plugins/LineChartTSDBDataRendererPlugin/useDataRefConfigIntoRendererPluginParams.ts'
import useSkipFirstRenderEffect from '@/shared/hooks/useSkipFirstRenderEffect.ts'
import LineChart from '@/shared/Charting/LineChart'
import ZoomPlugin from '@/shared/Charting/LineChart/plugins/LineChartZoomPlugin'
import PanPlugin from '@/shared/Charting/LineChart/plugins/LineChartPanPlugin'
import AxesWithGridPlugin from '@/shared/Charting/LineChart/plugins/LIneChartAxesWithGridPlugin'
import CrosshairPlugin from '@/shared/Charting/LineChart/plugins/LineChartCrosshairPlugin'
import TSDBDataRendererPlugin from '@/shared/Charting/LineChart/plugins/LineChartTSDBDataRendererPlugin'
import LineChartLegendPlugin from '@/shared/Charting/LineChart/plugins/LineChartLegendPlugin'
import liftChartEventEmitter from '@/pages/Equipment/pages/LiftPage/pages/LiftMonitoring/liftChart.eventEmitter.ts'
import liftMonitoringEventEmitter from '@/pages/Equipment/pages/LiftPage/pages/LiftMonitoring/liftMonitoring.eventEmitter.ts'
import EmptyPlaceholder from '@/shared/Common/EmptyPlaceholder'
import css from './lineChart.module.css'
import LineChartEventsPlugin from '@/shared/Charting/LineChart/plugins/LineChartEventsPlugin'
import useEquipment from '@/pages/Equipment/hooks/useEquipment.ts'
import cn from '@/utils/cn.ts'
import useFullscreenOverlay from '@/shared/hooks/useFullscreenOverlay.ts'
import useDataWithComputedRef, { IDataWithComputedProps } from '@/services/data/hooks/useDataWithComputedRef'
import { useRecoilValue } from 'recoil'
import parametersSelectors from '@/pages/Parameters/parameters.selectors'
import { IComputedParameterDef } from '@/pages/Parameters/parameters.types.ts'
import liftCardConfig from '../../../components/LiftCard/liftCard.config'

interface IProps {
  equipmentId: IEquipment['id']
  parametersConfig: Array<{ parameterId: IParameter['id']; color: string }>
  isLive: boolean
  startTime: number

  onParameterAdd?: (parameterId: IParameter['id']) => void
  onParameterRemove?: (parameterId: IParameter['id']) => void
}

const LiftAccelerationChart = (props: IProps) => {
  const parametersMap = useRecoilValue(parametersSelectors.getParametersMap)

  const equipment = useEquipment(props.equipmentId)
  const forceRerender = useForceRerender()

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const legendRef = useRef<HTMLDivElement>(null)

  const fullscreen = useFullscreenOverlay()

  const [rendererPlugin, setRendererPlugin] = useState<ITSDBDataRendererPlugin>(null)
  const [legendPlugin, setLegendPlugin] = useState<ILegendPlugin>(null)

  const [dataRefConfig, setDataRefConfig] = useState(getDatRefConfig(props, parametersMap))

  useSkipFirstRenderEffect(() => {
    // Если пользователь поменял дефолтный кадр в 45 секунд, передаем соответсвующую информацию
    if (rendererPlugin?.chart?.zoom?.xMin) {
      setDataRefConfig(
        getDatRefConfig(props, parametersMap, {
          interval: rendererPlugin?.chart?.zoom?.xMax - rendererPlugin?.chart?.zoom?.xMin,
        })
      )
    } else {
      setDataRefConfig(getDatRefConfig(props, parametersMap))
    }
  }, [props.equipmentId, props.parametersConfig])

  const dataRef = useDataWithComputedRef(dataRefConfig)
  const rendererPluginDataOptions = useDataRefConfigIntoRendererPluginParams(dataRefConfig, props.parametersConfig)

  const resizeCanvas = useCallback(() => {
    const sizeTarget = fullscreen.isFullscreen ? wrapperRef.current : fullscreen.inlineHostRef.current
    if (!sizeTarget || !canvasRef.current) return

    const width = sizeTarget.clientWidth
    const height = sizeTarget.clientHeight
    if (!width || !height) return

    canvasRef.current.width = width
    canvasRef.current.height = height
    canvasRef.current.style.height = height + 'px'
    canvasRef.current.style.width = width + 'px'

    rendererPlugin?.chart?.render()
  }, [fullscreen.isFullscreen, fullscreen.inlineHostRef, rendererPlugin])

  useLayoutEffect(() => {
    if (!rendererPlugin) return

    resizeCanvas()

    rendererPlugin.toggleLoadingState(dataRef.loading)

    forceRerender()
  }, [dataRef.loading, rendererPlugin, resizeCanvas, forceRerender, fullscreen.isFullscreen])

  useLayoutEffect(() => {
    if (!rendererPlugin) return

    const rafId = requestAnimationFrame(() => resizeCanvas())
    return () => cancelAnimationFrame(rafId)
  }, [fullscreen.isFullscreen, rendererPlugin, resizeCanvas])

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => resizeCanvas())
    const elementToObserve = fullscreen.isFullscreen ? wrapperRef.current : fullscreen.inlineHostRef.current
    if (elementToObserve) {
      observer.observe(elementToObserve)
    }
    return () => observer.disconnect()
  }, [fullscreen.isFullscreen, fullscreen.inlineHostRef, resizeCanvas])

  useEffect(() => {
    const handleResize = () => resizeCanvas()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [resizeCanvas])

  useSkipFirstRenderEffect(() => {
    if (rendererPlugin) {
      rendererPlugin.setDataRef(dataRef.dataRef, rendererPluginDataOptions)
    }
  }, [rendererPluginDataOptions])

  useEffect(() => {
    const canvas = canvasRef.current

    const lineChart = new LineChart(canvas, {
      datasets: [],
      xAxesConf: { tsFrom: dataRefConfig.initialTsFrom, tsTo: dataRefConfig.initialTsTo, type: 'time' },
    })
    if (!props.isLive) {
      lineChart.zoom.mode = 'manual'
      lineChart.zoom.setZoom({ xMin: dataRefConfig.initialTsFrom, xMax: dataRefConfig.initialTsTo }, 'pan-plugin')
    }

    const zoomPlugin = ZoomPlugin()
    zoomPlugin.init(lineChart)
    zoomPlugin.setLimits({ maxDelta: 1000 * 60 * 60 }) // 1 hour

    const panPlugin = PanPlugin()
    panPlugin.init(lineChart)

    const gridPlugin = AxesWithGridPlugin()
    gridPlugin.init(lineChart)

    const crosshairPlugin = CrosshairPlugin()
    crosshairPlugin.init(lineChart)

    const rendererPlugin = TSDBDataRendererPlugin()

    const legendPlugin = LineChartLegendPlugin()
    legendPlugin.setRef(legendRef)

    const eventsPlugin = LineChartEventsPlugin(equipment)

    lineChart.use(zoomPlugin)
    lineChart.use(panPlugin)
    lineChart.use(gridPlugin)
    lineChart.use(crosshairPlugin)
    lineChart.use(rendererPlugin)
    lineChart.use(legendPlugin)
    lineChart.use(eventsPlugin)

    rendererPlugin.setDataRef(dataRef.dataRef, rendererPluginDataOptions)

    setRendererPlugin(rendererPlugin)
    setLegendPlugin(legendPlugin)

    const eventsPluginLegendInterceptor = eventsPlugin.legendInterceptor()
    legendPlugin.addInterceptor(eventsPluginLegendInterceptor)

    lineChart.zoom.mode$.subscribe((mode) => {
      setDataRefConfig((prev) => ({ ...prev, live: mode === 'auto' }))
      forceRerender()

      liftChartEventEmitter.next({ state: { live: mode === 'auto', ts: lineChart.zoom.xMax } })
    })

    lineChart.zoom.changes$.subscribe((data) => {
      setDataRefConfig((prev) => ({ ...prev, initialTsFrom: data.xMin, initialTsTo: data.xMax }))

      liftChartEventEmitter.next({ state: { live: lineChart.zoom.mode === 'auto', ts: data.xMax } })
    })

    liftMonitoringEventEmitter.subscribe((ev) => {
      if (ev.state.live) {
        const now = Date.now()
        lineChart.zoom.switchMode('auto')
        lineChart.zoom.setZoom({ xMin: now - 45 * 1000, xMax: now }, 'data-plugin')
      } else {
        lineChart.zoom.switchMode('manual')
        lineChart.zoom.setZoom({ xMin: ev.state.ts - 45 * 1000, xMax: ev.state.ts }, 'zoom-plugin')
      }
    })

    return () => {
      rendererPlugin.destroy()
      lineChart.destroy()
      setRendererPlugin(null)
    }
  }, [])

  return (
    <>
      <div ref={fullscreen.inlineHostRef} className={css.inlineHost} />
      {createPortal(
        <div
          ref={wrapperRef}
          className={cn(css.chartWrapper, {
            [css.chartWrapperFullscreen]: fullscreen.isFullscreen,
          })}
        >
          {Boolean(legendPlugin) && legendPlugin.renderLegend()}
          {Boolean(rendererPlugin) &&
            rendererPlugin.renderHeading({
              onParameterAdd: props.onParameterAdd,
              onParameterRemove: props.onParameterRemove,
              onToggleFullscreen: fullscreen.toggle,
              isFullscreen: fullscreen.isFullscreen,
            })}
          <canvas ref={canvasRef} />
          {!props.parametersConfig.length && (
            <div className={css.emptyPlaceholderWrapper}>
              <EmptyPlaceholder inscription={`Добавьте переменные\nдля отображения на графике`} />
            </div>
          )}
        </div>,
        fullscreen.portalContainer
      )}
    </>
  )
}

const getDatRefConfig = (
  props: IProps,
  parametersMap: Record<IParameter['id'], IParameter>,
  config = { interval: 1000 * 45 }
): IDataWithComputedProps => {
  const startTime = props.isLive ? new Date().getTime() : props.startTime
  const computedParameters: IComputedParameterDef[] = []

  if (props.parametersConfig.some((p) => parametersMap[p.parameterId]?.sourceId === 'carSpeed')) {
    const carSpeedComputed = liftCardConfig.computed.find((p) => p.id === 'computed:carSpeed')
    if (carSpeedComputed) {
      computedParameters.push(carSpeedComputed)
    }
  }

  return {
    initialTsFrom: startTime - config.interval,
    live: props.isLive,
    initialTsTo: startTime,
    devices: [props.equipmentId],
    parameters: [...props.parametersConfig.map((p) => p.parameterId), ...computedParameters.map((p) => p.id)],
    aggregate: false,
    computed: computedParameters,
  }
}

export default memo(LiftAccelerationChart)
